import { Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { ResizableSplitLayoutProps } from './resizable-split-layout.interface';
import { ScrollContainerContext } from './scroll-container.context';

const LEFT_WIDTH_STORAGE_KEY = 'resizable-split-layout.leftWidth';

function getStoredLeftWidth(fallback: number): number {
  const raw = localStorage.getItem(LEFT_WIDTH_STORAGE_KEY);

  if (raw == null) {
    return fallback;
  }

  const parsed = Number(raw);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }

  return parsed;
}

function setStoredLeftWidth(value: number) {
  localStorage.setItem(LEFT_WIDTH_STORAGE_KEY, String(value));
}

export function ResizableSplitLayoutComponent({
  left,
  right,
  rightHeader,
  defaultLeftWidth = 350,
  minLeftWidth = 240,
  minRightWidth = 240,
}: ResizableSplitLayoutProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
  const [leftWidth, setLeftWidth] = useState(() => getStoredLeftWidth(defaultLeftWidth));
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    if (!isDragging) {
      return undefined;
    }

    const handlePointerMove = (event: PointerEvent) => {
      if (!containerRef.current) {
        return;
      }

      const bounds = containerRef.current.getBoundingClientRect();
      const nextLeftWidth = event.clientX - bounds.left;
      const maxLeftWidth = Math.max(minLeftWidth, bounds.width - minRightWidth);

      setLeftWidth(Math.min(Math.max(nextLeftWidth, minLeftWidth), maxLeftWidth));
    };

    const handlePointerUp = () => {
      setIsDragging(false);
    };

    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointermove', handlePointerMove);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, [isDragging, minLeftWidth, minRightWidth]);

  useEffect(() => {
    setStoredLeftWidth(leftWidth);
  }, [leftWidth]);

  return (
    <Box
      ref={containerRef}
      sx={{
        display: 'flex',
        width: '100%',
        height: '100%',
        minWidth: 0,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          width: leftWidth,
          minWidth: minLeftWidth,
          flexShrink: 0,
          minHeight: 0,
          overflow: 'auto',
        }}
      >
        {left}
      </Box>

      <Box
        role="separator"
        aria-orientation="vertical"
        onPointerDown={(event) => {
          event.preventDefault();
          setIsDragging(true);
        }}
        sx={{
          width: 8,
          flexShrink: 0,
          cursor: 'col-resize',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          touchAction: 'none',
          '&::before': {
            content: '""',
            width: 2,
            height: '100%',
            borderRadius: 999,
            bgcolor: isDragging ? 'primary.main' : 'divider',
          },
          '&:hover::before': {
            bgcolor: 'text.secondary',
          },
        }}
      />

      <Box
        sx={{
          flex: 1,
          minWidth: minRightWidth,
          minHeight: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {rightHeader ? (
          <Box
            component="header"
            sx={{
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: 1,
              minHeight: 48,
              px: 2,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            {rightHeader}
          </Box>
        ) : null}
        <ScrollContainerContext.Provider value={scrollContainer}>
          <Box
            ref={setScrollContainer}
            sx={{
              flex: 1,
              minHeight: 0,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {right}
          </Box>
        </ScrollContainerContext.Provider>
      </Box>
    </Box>
  );
}
